import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tasksApi, teamsApi } from '@/utils/api';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { CheckSquare, User, Check, SlidersHorizontal, X, Copy, Bookmark, Plus, Loader2, ChevronDown, ListChecks, ArrowUpDown, Settings2, Briefcase } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth } from '@/contexts/AuthContext';
import InlineTaskPanel from '@/components/InlineTaskPanel';
import { TagSearchInput } from '@/components/TagSearchInput';
import { AssigneeAvatar } from '@/components/AssigneeAvatar';
import { taskRYG, rygFromTasks, RYG_META, RYG_ORDER } from '@/utils/ryg';


export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [statusSel, setStatusSel] = useState(() => {
    const s = searchParams.get('status');
    return s ? s.split(',').filter(v => ['not_started', 'in_progress', 'done'].includes(v)) : [];
  });
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || 'all');
  const [assigneeFilter, setAssigneeFilter] = useState(searchParams.get('assignee') || 'all');
  // Everyone seen across loaded tasks — options for the "filter by person" menu.
  const [people, setPeople] = useState({});
  const mergePeople = (ts) => {
    setPeople(prev => {
      const next = { ...prev };
      ts.forEach(t => {
        (t.assignees || []).forEach(a => { if (a?.user_id) next[a.user_id] = { name: a.name, initials: a.initials }; });
        if (t.assignee_user_id && !next[t.assignee_user_id]) next[t.assignee_user_id] = { name: t.assignee_name, initials: t.assignee_initials };
      });
      return next;
    });
  };
  const [tagFilter, setTagFilter] = useState(searchParams.get('tag') || 'all');
  const [wsFilter, setWsFilter] = useState(searchParams.get('ws') || 'all');
  const [timeframe, setTimeframe] = useState(searchParams.get('tf') || 'all');
  const [customFrom, setCustomFrom] = useState(searchParams.get('from') || '');
  const [customTo, setCustomTo] = useState(searchParams.get('to') || '');
  const [expandedId, setExpandedId] = useState(null);
  const [openTaskObj, setOpenTaskObj] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [assignedToMeCount, setAssignedToMeCount] = useState(0);
  const [savedViews, setSavedViews] = useState([]);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [savingView, setSavingView] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [sortBy, setSortBy] = useState('due');
  const [openMenu, setOpenMenu] = useState(null); // 'status' | 'filter' | 'sort' | 'cust'
  const [hiddenCols, setHiddenCols] = useState({ ws: false, st: false, asg: false, due: false, pri: false });
  const [rygFilter, setRygFilter] = useState(() => {
    const r = searchParams.get('ryg');
    return ['red', 'yellow', 'green'].includes(r) ? r : null;
  });

  // Apply column-visibility to the app root (v2 uses .mlk-app.hide-* selectors).
  useEffect(() => {
    const root = document.querySelector('.mlk-app');
    if (!root) return;
    ['ws', 'st', 'asg', 'due', 'pri'].forEach(k => root.classList.toggle(`hide-${k}`, !!hiddenCols[k]));
    return () => { ['ws', 'st', 'asg', 'due', 'pri'].forEach(k => root.classList.remove(`hide-${k}`)); };
  }, [hiddenCols]);

  // Close any open toolbar menu on outside click / Escape.
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e) => { if (!e.target.closest('[data-menuroot]')) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [openMenu]);

  // Teammates across my workspaces are always offered, even with zero tasks yet.
  useEffect(() => {
    teamsApi.list().then(r => {
      const map = {};
      (r.data.teams || []).forEach(t => {
        if (!t.is_member) return;
        (t.members || []).forEach(m => {
          if (!m.user_id) return;
          const nm = m.name || m.email || 'Member';
          map[m.user_id] = { name: nm, initials: nm.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() };
        });
      });
      setPeople(prev => ({ ...map, ...prev }));
    }).catch(() => {});
  }, []);

  // Unfiltered total, fetched once, for the "X of Y" filtered-view summary + assignee filter options.
  useEffect(() => {
    tasksApi.list({}).then(r => {
      const ts = r.data.tasks || [];
      setTotalCount(ts.length);
      mergePeople(ts);
    }).catch(() => {});
  }, []);

  // Count of open tasks assigned to me — powers the "Assigned to me" badge.
  const loadAssignedCount = useCallback(() => {
    tasksApi.list({ assignee: 'me' })
      .then(r => setAssignedToMeCount((r.data.tasks || []).filter(t => t.status !== 'done').length))
      .catch(() => {});
  }, []);
  useEffect(() => { loadAssignedCount(); }, [loadAssignedCount]);

  useEffect(() => {
    tasksApi.savedViews().then(r => setSavedViews(r.data.views || [])).catch(() => {});
  }, []);

  // Keep the URL in sync so filtered views are shareable and survive refresh.
  useEffect(() => {
    const p = {};
    if (search) p.q = search;
    if (statusSel.length) p.status = statusSel.join(',');
    if (priorityFilter !== 'all') p.priority = priorityFilter;
    if (assigneeFilter !== 'all') p.assignee = assigneeFilter;
    if (tagFilter !== 'all') p.tag = tagFilter;
    if (wsFilter !== 'all') p.ws = wsFilter;
    if (timeframe !== 'all') p.tf = timeframe;
    if (timeframe === 'custom' && customFrom) p.from = customFrom;
    if (timeframe === 'custom' && customTo) p.to = customTo;
    if (rygFilter) p.ryg = rygFilter;
    setSearchParams(p, { replace: true });
  }, [search, statusSel, priorityFilter, assigneeFilter, tagFilter, wsFilter, timeframe, customFrom, customTo, rygFilter, setSearchParams]);

  const timeframeRange = useCallback(() => {
    const iso = (d) => d.toISOString().slice(0, 10);
    const today = new Date();
    if (timeframe === 'today') return { due_from: iso(today), due_to: iso(today) };
    if (timeframe === 'week') { const e = new Date(); e.setDate(e.getDate() + 7); return { due_from: iso(today), due_to: iso(e) }; }
    if (timeframe === 'month') { const e = new Date(); e.setDate(e.getDate() + 30); return { due_from: iso(today), due_to: iso(e) }; }
    if (timeframe === 'overdue') { const y = new Date(); y.setDate(y.getDate() - 1); return { due_to: iso(y) }; }
    if (timeframe === 'custom') { const r = {}; if (customFrom) r.due_from = customFrom; if (customTo) r.due_to = customTo; return r; }
    return {};
  }, [timeframe, customFrom, customTo]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    try {
      const params = {};
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (assigneeFilter !== 'all') params.assignee = assigneeFilter;
      if (search) params.search = search;
      Object.assign(params, timeframeRange());
      const res = await tasksApi.list(params);
      setTasks(res.data.tasks || []);
      mergePeople(res.data.tasks || []);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [priorityFilter, assigneeFilter, search, timeframeRange]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const openTask = (task) => {
    setExpandedId(prev => {
      const opening = prev !== task.id;
      if (opening && task.has_unseen_activity) {
        setTasks(ts => ts.map(t => t.id === task.id ? { ...t, has_unseen_activity: false } : t));
      }
      setOpenTaskObj(opening ? task : null);
      if (opening) tasksApi.get(task.id).then(r => setOpenTaskObj(r.data)).catch(() => {});
      return opening ? task.id : null;
    });
  };
  const closeDrawer = () => { setExpandedId(null); setOpenTaskObj(null); };

  const assignMe = async (task) => {
    if (!user?.id) return;
    setAssigningId(task.id);
    setTasks(prev => prev.map(t => t.id === task.id
      ? { ...t, assignee_user_id: user.id, assignee_name: user.name, assignee_initials: (user.name || '?').slice(0, 2).toUpperCase() }
      : t));
    try {
      const res = await tasksApi.assign(task.id, user.id);
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, ...res.data } : t));
      toast.success('Assigned to you');
    } catch (e) {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, assignee_user_id: null, assignee_name: null } : t));
      toast.error(e?.response?.data?.detail || 'Could not assign');
    } finally {
      setAssigningId(null);
    }
  };

  // Inline add-row → create a standalone task, then refresh.
  const createTask = async (extra = {}) => {
    const title = newTitle.trim();
    if (!title) { setAddingRow(null); return; }
    setCreating(true);
    try {
      await tasksApi.create({ title, ...extra });
      setNewTitle('');
      setAddingRow(null);
      await load();
      toast.success('Task added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add task'); }
    finally { setCreating(false); }
  };

  const allTags = [...new Set(tasks.flatMap(t => [...(t.tags || []), ...(t.task_tags || [])]))].sort((a, b) => a.localeCompare(b));
  const tagCounts = {};
  tasks.forEach(t => [...(t.tags || []), ...(t.task_tags || [])].forEach(tag => { tagCounts[tag] = (tagCounts[tag] || 0) + 1; }));
  const tagsByUsage = [...allTags].sort((a, b) => (tagCounts[b] - tagCounts[a]) || a.localeCompare(b));
  const taskStatusKey = (t) => t.status === 'done' ? 'done' : t.status === 'in_progress' ? 'in_progress' : 'not_started';
  const statusPass = (t) => statusSel.length === 0 ? taskStatusKey(t) !== 'done' : statusSel.includes(taskStatusKey(t));
  const workspaces = [...new Map(tasks.filter(t => t.workspace_id).map(t => [t.workspace_id, t.team_name || 'Workspace'])).entries()].map(([id, name]) => ({ id, name }));
  const scopeFiltered = tasks
    .filter(t => wsFilter === 'all' || t.workspace_id === wsFilter)
    .filter(t => tagFilter === 'all' || (t.tags || []).includes(tagFilter) || (t.task_tags || []).includes(tagFilter));
  // RYG counts reflect ALL tasks in scope (regardless of the status dropdown) so "Done" is meaningful.
  const rygCounts = rygFromTasks(scopeFiltered);
  // A RYG filter takes over the status view (e.g. "Done" reveals completed tasks the default hides).
  const visibleTasks = rygFilter
    ? scopeFiltered.filter(t => taskRYG(t) === rygFilter)
    : scopeFiltered.filter(statusPass);

  // Order tasks so subtasks render directly beneath their parent (single level).
  const orderedTasks = (() => {
    const childrenByParent = {};
    visibleTasks.forEach(t => { if (t.parent_task_id) (childrenByParent[t.parent_task_id] = childrenByParent[t.parent_task_id] || []).push(t); });
    const out = [];
    visibleTasks.forEach(t => {
      if (t.parent_task_id) return;
      out.push(t);
      (childrenByParent[t.id] || []).forEach(c => out.push({ ...c, _isChild: true }));
    });
    // Orphan subtasks whose parent isn't in the current view.
    visibleTasks.forEach(t => {
      if (t.parent_task_id && !visibleTasks.some(p => p.id === t.parent_task_id)) out.push(t);
    });
    return out;
  })();

  const pending = visibleTasks.filter(t => t.status !== 'done');
  const completed = visibleTasks.filter(t => t.status === 'done');
  const activeFilters = (statusSel.length ? 1 : 0) + (priorityFilter !== 'all' ? 1 : 0) + (assigneeFilter !== 'all' ? 1 : 0) + (tagFilter !== 'all' ? 1 : 0) + (wsFilter !== 'all' ? 1 : 0) + (timeframe !== 'all' ? 1 : 0);
  const hasActiveFilters = activeFilters > 0 || !!search;

  const STATUS_LABELS = { not_started: 'Not started', in_progress: 'In progress', done: 'Completed' };
  const TF_LABELS = { today: 'Due today', week: 'Next 7 days', month: 'Next 30 days', overdue: 'Overdue', custom: 'Custom range' };
  const wsName = (id) => (workspaces.find(w => w.id === id) || {}).name || 'Workspace';
  const filterLabels = [
    statusSel.length && statusSel.map(s => STATUS_LABELS[s]).join(' / '),
    priorityFilter !== 'all' && priorityFilter.charAt(0).toUpperCase() + priorityFilter.slice(1),
    assigneeFilter === 'me' && 'Assigned to you',
    assigneeFilter === 'unassigned' && 'Unassigned',
    !['all', 'me', 'unassigned'].includes(assigneeFilter) && `Assignee: ${people[assigneeFilter]?.name || 'person'}`,
    wsFilter !== 'all' && wsName(wsFilter),
    timeframe !== 'all' && TF_LABELS[timeframe],
    tagFilter !== 'all' && tagFilter,
    search && `"${search}"`,
  ].filter(Boolean);

  const clearFilters = () => {
    setStatusSel([]);
    setPriorityFilter('all');
    setAssigneeFilter('all');
    setTagFilter('all');
    setWsFilter('all');
    setTimeframe('all');
    setCustomFrom(''); setCustomTo('');
    setSearch('');
  };

  const currentFilters = () => {
    const f = {};
    if (statusSel.length) f.status = statusSel.join(',');
    if (priorityFilter !== 'all') f.priority = priorityFilter;
    if (assigneeFilter !== 'all') f.assignee = assigneeFilter;
    if (wsFilter !== 'all') f.ws = wsFilter;
    if (timeframe !== 'all') f.tf = timeframe;
    if (timeframe === 'custom' && customFrom) f.from = customFrom;
    if (timeframe === 'custom' && customTo) f.to = customTo;
    if (tagFilter !== 'all') f.tag = tagFilter;
    if (search) f.q = search;
    return f;
  };

  const applyView = (v) => {
    const f = v.filters || {};
    setStatusSel(f.status ? f.status.split(',').filter(s => ['not_started', 'in_progress', 'done'].includes(s)) : []);
    setPriorityFilter(f.priority || 'all');
    setAssigneeFilter(f.assignee || 'all');
    setWsFilter(f.ws || 'all');
    setTimeframe(f.tf || 'all');
    setCustomFrom(f.from || ''); setCustomTo(f.to || '');
    setTagFilter(f.tag || 'all');
    setSearch(f.q || '');
  };

  const isViewActive = (v) => {
    const f = v.filters || {};
    return (f.status || '') === statusSel.join(',')
      && (f.priority || 'all') === priorityFilter
      && (f.assignee || 'all') === assigneeFilter
      && (f.ws || 'all') === wsFilter
      && (f.tf || 'all') === timeframe
      && (f.tag || 'all') === tagFilter
      && (f.q || '') === search;
  };

  const saveCurrentView = async (e) => {
    e?.preventDefault();
    const name = viewName.trim();
    if (!name) return;
    setSavingView(true);
    try {
      const res = await tasksApi.saveView(name, currentFilters());
      setSavedViews(prev => [...prev, res.data]);
      setViewName('');
      setSaveViewOpen(false);
      toast.success('View saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save view'); }
    finally { setSavingView(false); }
  };

  const removeView = async (id) => {
    try {
      await tasksApi.deleteView(id);
      setSavedViews(prev => prev.filter(v => v.id !== id));
    } catch { toast.error('Failed to delete view'); }
  };

  const STPILL = (s) => s === 'done' ? { c: 'cp', l: 'Completed' }
    : s === 'in_progress' ? { c: 'ip', l: 'In progress' }
    : { c: 'ns', l: 'Not started' };
  const PRIPILL = (p) => p === 'high' ? { c: 'hi', l: 'High' }
    : p === 'low' ? { c: 'lo', l: 'Low' } : { c: 'md', l: 'Medium' };
  const dueMeta = (d) => {
    if (!d) return null;
    const today = new Date().toISOString().slice(0, 10);
    const cls = d < today ? 'over' : d === today ? 'today' : '';
    let label = d;
    try {
      const dt = new Date(d + 'T00:00:00');
      label = d === today ? 'Today' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { /* keep raw */ }
    return { cls, label };
  };

  const TaskRow = (task) => {
    const st = STPILL(task.status);
    const pr = PRIPILL(task.priority);
    const due = dueMeta(task.due_date);
    const selected = expandedId === task.id;
    return (
      <div
        key={task.id}
        className={`row${task._isChild ? ' sub' : ''}${task.status === 'done' ? ' done-row' : ''}${selected ? ' sel' : ''}`}
        data-testid={task._isChild ? `subtask-row-${task.id}` : `task-row-${task.id}`}
        onClick={() => openTask(task)}
      >
        <div className="r-name">
          <span className="tt" data-testid={`task-title-${task.id}`}>
            {!task._isChild && (
              <span className="ryg-dot" data-testid={`task-ryg-${task.id}`}
                title={RYG_META[taskRYG(task)].label}
                style={{ background: RYG_META[taskRYG(task)].color, marginRight: 8, display: 'inline-block', verticalAlign: 'middle' }} />
            )}
            {task.task_ref && <span className="lab" style={{ marginRight: 7, opacity: .6 }}>{task.task_ref}</span>}
            {task.title}
          </span>
          {task.subtask_total > 0 && (
            <span className="subct" title={`${task.subtask_done || 0} of ${task.subtask_total} subtasks done`}>
              <CheckSquare />{task.subtask_done || 0}/{task.subtask_total}
            </span>
          )}
          {task.has_unseen_activity && (
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--blue)' }} title="New activity" data-testid={`task-new-activity-${task.id}`} />
          )}
          {task.possible_duplicate && (
            <span className="src-chip" style={{ background: 'var(--butter-t)', color: 'var(--butter-tx)' }} title="Possible duplicate">
              <Copy size={10} />dup
            </span>
          )}
        </div>
        <div className="r-ws">{task.team_name ? <span className="ws-tag" title={task.team_name}>{task.team_name}</span> : <span className="ws-none">—</span>}</div>
        <div className="r-st">{task.status ? <span className={`stpill ${st.c}`}>{st.l}</span> : null}</div>
        <div className="r-asg">
          {task.assignee_name
            ? <AssigneeAvatar task={task} size={26} />
            : (
              <button
                className="asg-empty"
                data-tip="Assign to me"
                data-testid={`task-assign-me-${task.id}`}
                disabled={assigningId === task.id}
                onClick={(e) => { e.stopPropagation(); assignMe(task); }}
              >
                <User />
              </button>
            )}
        </div>
        <div className={`r-due${due?.cls ? ' ' + due.cls : ''}`}>{due?.label || ''}</div>
        <div className="r-pri"><span className={`pill ${pr.c}`}>{pr.l}</span></div>
      </div>
    );
  };

  const PRI_RANK = { high: 0, medium: 1, low: 2 };
  const sortTasks = (arr) => {
    if (sortBy === 'none') return arr;
    const a = [...arr];
    if (sortBy === 'due') a.sort((x, y) => (x.due_date || '9999').localeCompare(y.due_date || '9999'));
    else if (sortBy === 'pri') a.sort((x, y) => (PRI_RANK[x.priority] ?? 1) - (PRI_RANK[y.priority] ?? 1));
    else if (sortBy === 'alpha') a.sort((x, y) => (x.title || '').localeCompare(y.title || ''));
    return a;
  };

  // Semantic sections (match v2 design): group open tasks by urgency / ownership.
  const todayStr = new Date().toISOString().slice(0, 10);
  const weekEnd = new Date(Date.now() + 6 * 864e5).toISOString().slice(0, 10);
  const meId = user?.id;
  const bucketOf = (t) => {
    if (t.status === 'done') return 'done';
    const mine = meId && ((t.assignees || []).some(a => a?.user_id === meId) || t.assignee_user_id === meId || (!t.assignee_user_id && !(t.assignees || []).length));
    if (!mine) return 'waiting';
    if (t.due_date && t.due_date <= todayStr) return 'today';
    if (t.due_date && t.due_date <= weekEnd) return 'week';
    return 'later';
  };
  const GROUP_DEFS = [
    ['today', 'Needs you today'],
    ['week', 'This week'],
    ['waiting', 'Waiting on others'],
    ['later', 'Later'],
    ['done', 'Completed'],
  ];
  const bucketByTask = {};
  orderedTasks.forEach(t => { if (!t._isChild) bucketByTask[t.id] = bucketOf(t); });
  const grouped = {};
  orderedTasks.forEach(t => {
    const key = t._isChild ? (bucketByTask[t.parent_task_id] || bucketOf(t)) : bucketByTask[t.id];
    (grouped[key] = grouped[key] || []).push(t);
  });
  const threadSort = (list) => {
    if (sortBy === 'none') return list;
    const kids = {};
    list.forEach(t => { if (t._isChild) (kids[t.parent_task_id] = kids[t.parent_task_id] || []).push(t); });
    const out = [];
    sortTasks(list.filter(t => !t._isChild)).forEach(t => { out.push(t); (kids[t.id] || []).forEach(k => out.push(k)); });
    return out;
  };
  const groups = GROUP_DEFS
    .map(([key, label]) => ({ key, label, tasks: threadSort(grouped[key] || []) }))
    .filter(g => g.tasks.length > 0);

  const AddRow = ({ groupKey }) => (
    addingRow === groupKey ? (
      <div className="add-row" style={{ cursor: 'default' }}>
        <input
          autoFocus
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') createTask(groupKey === 'today' ? { due_date: todayStr } : {}); if (e.key === 'Escape') { setAddingRow(null); setNewTitle(''); } }}
          onBlur={() => createTask(groupKey === 'today' ? { due_date: todayStr } : {})}
          placeholder="Task title, then Enter…"
          data-testid="tasks-add-input"
          disabled={creating}
          className="flex-1 bg-transparent outline-none text-[13.5px] text-[color:var(--ink)]"
          style={{ fontFamily: 'Work' }}
        />
      </div>
    ) : (
      <div className="add-row" onClick={() => { setNewTitle(''); setAddingRow(groupKey); }} data-testid={`tasks-add-row-${groupKey}`}>
        <Plus size={13} />Add task…
      </div>
    )
  );

  return (
    <div className="pagewrap">

      {/* Header */}
      <div className="page-head">
        <div>
          <h1>Tasks</h1>
          <p className="sub">
            {pending.length} open · {completed.length} completed
          </p>
        </div>
      </div>

      {/* RYG status filter — drill-down health across all tasks */}
      <div className="ryg-bar" data-testid="tasks-ryg-bar">
        <span className="ryg-lbl">Status</span>
        {RYG_ORDER.map(k => (
          <button key={k} className={`ryg-chip${rygFilter === k ? ' on' : ''}`} data-testid={`tasks-ryg-${k}`}
            onClick={() => setRygFilter(rygFilter === k ? null : k)}
            title={rygFilter === k ? 'Clear filter' : `Show ${RYG_META[k].label.toLowerCase()} tasks`}
            style={rygFilter === k ? { borderColor: RYG_META[k].color, background: RYG_META[k].bg } : undefined}>
            <span className="ryg-dot" style={{ background: RYG_META[k].color }} />
            {RYG_META[k].label}<b>{rygCounts[k]}</b>
          </button>
        ))}
        {rygFilter && <button className="ryg-clear" data-testid="tasks-ryg-clear" onClick={() => setRygFilter(null)}><X size={13} />Clear</button>}
      </div>


      {/* Saved views — one-tap recurring triage filters */}
      {(savedViews.length > 0 || hasActiveFilters) && (
        <div className="flex items-center flex-wrap gap-2 mb-3" data-testid="tasks-saved-views">
          {savedViews.map(v => (
            <span key={v.id}
              className="group inline-flex items-center rounded-full border transition-colors"
              style={isViewActive(v)
                ? { background: '#6994c6', borderColor: '#6994c6', color: 'white' }
                : { background: 'white', borderColor: 'rgba(105,148,198,0.35)', color: '#4f74a0' }}>
              <button
                onClick={() => applyView(v)}
                data-testid={`saved-view-${v.id}`}
                className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 text-xs font-semibold"
                title={`Apply view: ${v.name}`}>
                <Bookmark size={11} />{v.name}
              </button>
              <button
                onClick={() => removeView(v.id)}
                data-testid={`saved-view-delete-${v.id}`}
                className="mr-1.5 rounded-full p-0.5 hover:bg-black/10"
                title="Delete view">
                <X size={11} />
              </button>
            </span>
          ))}
          {hasActiveFilters && (
            <Popover open={saveViewOpen} onOpenChange={setSaveViewOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="tasks-save-view-button"
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-dashed text-xs font-semibold hover:bg-secondary transition-colors"
                  style={{ color: '#5a7d52', borderColor: 'rgba(90,125,82,0.5)' }}>
                  <Plus size={12} />Save view
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 bg-white border-border p-2.5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Save current filters</p>
                {filterLabels.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mb-2">{filterLabels.join(', ')}</p>
                )}
                <form onSubmit={saveCurrentView} className="flex gap-1.5">
                  <input autoFocus value={viewName} onChange={e => setViewName(e.target.value)}
                    placeholder="View name…" data-testid="tasks-save-view-input"
                    className="flex-1 min-w-0 rounded-lg border border-border px-2 py-1 text-sm outline-none focus:border-[#5a7d52]" style={{ fontSize: '16px' }} />
                  <button type="submit" disabled={savingView || !viewName.trim()} data-testid="tasks-save-view-submit"
                    className="inline-flex items-center justify-center px-2.5 rounded-lg text-white disabled:opacity-50" style={{ background: '#5a7d52' }}>
                    {savingView ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  </button>
                </form>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}


      {/* Active-filter summary */}
      {hasActiveFilters && (
        <div
          className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-3 px-3 py-2 rounded-xl border text-xs"
          style={{ background: 'rgba(105,148,198,0.07)', borderColor: 'rgba(105,148,198,0.25)', color: '#4f74a0' }}
          data-testid="tasks-filter-summary"
        >
          <span className="font-semibold">
            Showing {visibleTasks.length}{totalCount != null ? ` of ${totalCount}` : ''} task{visibleTasks.length === 1 ? '' : 's'}
          </span>
          {filterLabels.length > 0 && (
            <span className="text-muted-foreground">· {filterLabels.join(', ')}</span>
          )}
          <button
            onClick={clearFilters}
            data-testid="tasks-clear-filters-button"
            className="ml-auto inline-flex items-center gap-1 font-semibold px-2 py-1 rounded-lg hover:bg-white/70 transition-colors"
          >
            <X size={12} />Clear filters
          </button>
        </div>
      )}

      {/* Search + filter toggle */}
      <div className="toolbar">
        <div className="tool-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <TagSearchInput
            testId="tasks-search-input"
            value={search}
            onChange={setSearch}
            tags={allTags}
            placeholder="Search tasks — use #tag to search by tag"
            inputClassName="w-full border-0 bg-transparent outline-none"
          />
        </div>
        <div className="tool-right">
          {/* Status (multi-select) */}
          <div className="dd" data-menuroot>
            <button className={`tbtn${statusSel.length ? ' on' : ''}`} data-testid="tasks-status-btn"
              onClick={() => setOpenMenu(m => m === 'status' ? null : 'status')}>
              <ListChecks size={15} />{statusSel.length ? (statusSel.length === 1 ? STATUS_LABELS[statusSel[0]] : `${statusSel.length} statuses`) : 'Status'}
            </button>
            <div className={`menu dd-menu${openMenu === 'status' ? ' open' : ''}`}>
              <div className="mlab">Show status · pick any</div>
              {[['not_started', 'ns', 'Not started'], ['in_progress', 'ip', 'In progress'], ['done', 'cp', 'Completed']].map(([val, c, label]) => (
                <button key={val} className={`mi${statusSel.includes(val) ? ' on' : ''}`} data-testid={`tasks-status-${val}`}
                  onClick={() => setStatusSel(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])}>
                  <span className={`stpill ${c}`}>{label}</span>
                  <span className="mcheck"><Check size={14} /></span>
                </button>
              ))}
              <div className="msep" />
              <button className={`mi${statusSel.length === 0 ? ' on' : ''}`} data-testid="tasks-status-all"
                onClick={() => { setStatusSel([]); setOpenMenu(null); }}>Reset (open tasks)<span className="mcheck"><Check size={14} /></span></button>
            </div>
          </div>

          {/* Workspace filter */}
          {workspaces.length > 0 && (
            <div className="dd" data-menuroot>
              <button className={`tbtn${wsFilter !== 'all' ? ' on' : ''}`} data-testid="tasks-ws-btn"
                onClick={() => setOpenMenu(m => m === 'ws' ? null : 'ws')}>
                <Briefcase size={15} />{wsFilter !== 'all' ? wsName(wsFilter) : 'Workspace'}
              </button>
              <div className={`menu dd-menu${openMenu === 'ws' ? ' open' : ''}`}>
                <div className="mlab">Filter by workspace</div>
                {workspaces.map(w => (
                  <button key={w.id} className={`mi${wsFilter === w.id ? ' on' : ''}`} data-testid={`tasks-ws-${w.id}`}
                    onClick={() => { setWsFilter(wsFilter === w.id ? 'all' : w.id); setOpenMenu(null); }}>
                    {w.name}<span className="mcheck"><Check size={14} /></span>
                  </button>
                ))}
                <div className="msep" />
                <button className={`mi${wsFilter === 'all' ? ' on' : ''}`} data-testid="tasks-ws-all"
                  onClick={() => { setWsFilter('all'); setOpenMenu(null); }}>All workspaces<span className="mcheck"><Check size={14} /></span></button>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="dd" data-menuroot>
            <button className={`tbtn${(priorityFilter !== 'all' || assigneeFilter !== 'all' || timeframe !== 'all') ? ' on' : ''}`} data-testid="tasks-filter-btn"
              onClick={() => setOpenMenu(m => m === 'filter' ? null : 'filter')}>
              <SlidersHorizontal size={15} />Filter
              {activeFilters > 0 && <span className="fct">{activeFilters}</span>}
            </button>
            <div className={`menu dd-menu${openMenu === 'filter' ? ' open' : ''}`}>
              <div className="mlab">Assignee</div>
              <button className={`mi${assigneeFilter === 'me' ? ' on' : ''}`} data-testid="tasks-filter-mine"
                onClick={() => setAssigneeFilter(assigneeFilter === 'me' ? 'all' : 'me')}>My tasks only<span className="mcheck"><Check size={14} /></span></button>
              <button className={`mi${assigneeFilter === 'unassigned' ? ' on' : ''}`} data-testid="tasks-filter-unassigned"
                onClick={() => setAssigneeFilter(assigneeFilter === 'unassigned' ? 'all' : 'unassigned')}>Unassigned<span className="mcheck"><Check size={14} /></span></button>
              {Object.entries(people).filter(([uid]) => uid !== meId).map(([uid, p]) => (
                <button key={uid} className={`mi${assigneeFilter === uid ? ' on' : ''}`} data-testid={`tasks-filter-person-${uid}`}
                  onClick={() => setAssigneeFilter(assigneeFilter === uid ? 'all' : uid)}>
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ background: '#6994c6' }}>{p.initials || (p.name || '?').slice(0, 2).toUpperCase()}</span>
                    <span className="truncate">{p.name || 'Unknown'}</span>
                  </span>
                  <span className="mcheck"><Check size={14} /></span>
                </button>
              ))}
              <div className="msep" />
              <div className="mlab">Priority</div>
              {[['high', 'hi', 'High'], ['medium', 'md', 'Med'], ['low', 'lo', 'Low']].map(([val, c, label]) => (
                <button key={val} className={`mi${priorityFilter === val ? ' on' : ''}`} data-testid={`tasks-filter-pri-${val}`}
                  onClick={() => setPriorityFilter(priorityFilter === val ? 'all' : val)}>
                  <span className={`pill ${c}`}>{label}</span><span className="mcheck"><Check size={14} /></span>
                </button>
              ))}
              <div className="msep" />
              <div className="mlab">Due</div>
              <button className={`mi${timeframe === 'overdue' ? ' on' : ''}`} data-testid="tasks-filter-due-overdue"
                onClick={() => setTimeframe(timeframe === 'overdue' ? 'all' : 'overdue')}>Overdue<span className="mcheck"><Check size={14} /></span></button>
              <button className={`mi${timeframe === 'today' ? ' on' : ''}`} data-testid="tasks-filter-due-today"
                onClick={() => setTimeframe(timeframe === 'today' ? 'all' : 'today')}>Due today<span className="mcheck"><Check size={14} /></span></button>
              <button className={`mi${timeframe === 'week' ? ' on' : ''}`} data-testid="tasks-filter-due-week"
                onClick={() => setTimeframe(timeframe === 'week' ? 'all' : 'week')}>Next 7 days<span className="mcheck"><Check size={14} /></span></button>
              <button className={`mi${timeframe === 'month' ? ' on' : ''}`} data-testid="tasks-filter-due-month"
                onClick={() => setTimeframe(timeframe === 'month' ? 'all' : 'month')}>Next 30 days<span className="mcheck"><Check size={14} /></span></button>
              <div className="px-3 py-2" onClick={e => e.stopPropagation()}>
                <div className={`text-[11px] font-semibold mb-1 ${timeframe === 'custom' ? 'text-[#6994c6]' : 'text-muted-foreground'}`}>Custom range</div>
                <div className="flex items-center gap-1.5">
                  <input type="date" value={customFrom} data-testid="tasks-filter-custom-from"
                    onChange={e => { setCustomFrom(e.target.value); setTimeframe('custom'); }}
                    className="flex-1 min-w-0 rounded-md border border-border px-1.5 py-1 text-xs outline-none focus:border-[#6994c6]" />
                  <span className="text-xs text-muted-foreground">→</span>
                  <input type="date" value={customTo} data-testid="tasks-filter-custom-to"
                    onChange={e => { setCustomTo(e.target.value); setTimeframe('custom'); }}
                    className="flex-1 min-w-0 rounded-md border border-border px-1.5 py-1 text-xs outline-none focus:border-[#6994c6]" />
                </div>
              </div>
              <div className="msep" />
              <button className="mi" data-testid="tasks-filter-clear"
                onClick={() => { setPriorityFilter('all'); setAssigneeFilter('all'); setTimeframe('all'); setCustomFrom(''); setCustomTo(''); setTagFilter('all'); setStatusSel([]); setOpenMenu(null); }}>
                <X size={14} />Clear filters
              </button>
            </div>
          </div>

          {/* Sort */}
          <div className="dd" data-menuroot>
            <button className={`tbtn${sortBy !== 'none' ? ' on' : ''}`} data-testid="tasks-sort-btn"
              onClick={() => setOpenMenu(m => m === 'sort' ? null : 'sort')}>
              <ArrowUpDown size={15} />Sort
            </button>
            <div className={`menu dd-menu${openMenu === 'sort' ? ' open' : ''}`}>
              <div className="mlab">Sort tasks by</div>
              {[['due', 'Due date'], ['pri', 'Priority']].map(([val, label]) => (
                <button key={val} className={`mi${sortBy === val ? ' on' : ''}`} data-testid={`tasks-sort-${val}`}
                  onClick={() => { setSortBy(sortBy === val ? 'none' : val); setOpenMenu(null); }}>{label}<span className="mcheck"><Check size={14} /></span></button>
              ))}
            </div>
          </div>

          {/* Customize columns */}
          <div className="dd" data-menuroot>
            <button className="tbtn" data-testid="tasks-customize-btn"
              onClick={() => setOpenMenu(m => m === 'cust' ? null : 'cust')}>
              <Settings2 size={15} />Customize
            </button>
            <div className={`menu dd-menu${openMenu === 'cust' ? ' open' : ''}`}>
              <div className="mlab">Show columns</div>
              {[['ws', 'Workspace'], ['st', 'Status'], ['asg', 'Assignee'], ['due', 'Due date'], ['pri', 'Priority']].map(([k, label]) => (
                <button key={k} className={`mi${!hiddenCols[k] ? ' on' : ''}`} data-testid={`tasks-col-${k}`}
                  onClick={() => setHiddenCols(c => ({ ...c, [k]: !c[k] }))}>{label}<span className="mcheck"><Check size={14} /></span></button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Old expandable filter panel removed — superseded by the v2 toolbar dropdowns (Status / Filter / Sort / Customize). Tag filtering is available via the search box using #tag. */}
      <div className="list-wrap" data-testid="tasks-list">
        {loading ? (
          <div className="space-y-0">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-[40px] w-full rounded-none" />)}
          </div>
        ) : visibleTasks.length === 0 ? (
          <div className="py-14 text-center px-4">
            <CheckSquare size={28} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No tasks found</p>
            <p className="text-xs text-muted-foreground">
              {search || activeFilters > 0 ? 'Try adjusting your filters' : 'Create an Action Card to generate tasks'}
            </p>
          </div>
        ) : (
          <>
            <div className="cols">
              <div className="c-name">Task name</div>
              <div className="c-ws">Workspace</div>
              <div className="c-st">Status</div>
              <div className="c-asg">Assignee</div>
              <div className="c-due">Due date</div>
              <div className="c-pri">Priority</div>
            </div>

            {groups.map(g => {
              const isClosed = g.key === 'done' ? !doneOpen : !!collapsed[g.key];
              const toggle = () => g.key === 'done' ? setDoneOpen(o => !o) : setCollapsed(c => ({ ...c, [g.key]: !c[g.key] }));
              return (
                <div key={g.key} className={`section${isClosed ? ' closed' : ''}`} data-testid={`tasks-section-${g.key}`}>
                  <div className="sec-head">
                    <button className="sec-caret" onClick={toggle} data-testid={`tasks-section-toggle-${g.key}`} aria-label={`Toggle ${g.label}`}>
                      <ChevronDown />
                    </button>
                    <span className="sec-name">{g.label}</span>
                    <span className="sec-count">{g.tasks.filter(t => !t._isChild).length}</span>
                  </div>
                  <div className="sec-body">
                    {g.tasks.map(TaskRow)}
                    {g.key !== 'done' && g.key !== 'waiting' && <AddRow groupKey={g.key} />}
                  </div>
                </div>
              );
            })}
            <div className="add-section-row" data-testid="tasks-add-section"
              onClick={() => toast.message('Sections are organized automatically by due date and ownership.')}>
              <Plus size={14} />Add section
            </div>
          </>
        )}
      </div>

      {/* Right-side detail drawer — hosts the full inline task editor */}
      <div
        className="detail-bd"
        style={{ display: expandedId ? 'block' : 'none' }}
        onClick={closeDrawer}
        data-testid="task-detail-backdrop"
      />
      <div className={`detail${expandedId ? ' open' : ''}`} data-testid="task-detail-drawer">
        {expandedId && (() => {
          const task = openTaskObj || visibleTasks.find(t => t.id === expandedId);
          if (!task) return null;
          return (
            <InlineTaskPanel
              task={task}
              canAssign={!!(task.can_assign ?? (task.is_shared === false))}
              onChanged={load}
              onClose={closeDrawer}
            />
          );
        })()}
      </div>
    </div>
  );
}
